import { action, makeObservable, observable } from 'mobx';
import {
    ColumnFiltersMap,
    ColumnSearchMap,
    ColumnSort,
    ColumnSortModel,
    FilterValue
} from '../FilterPopup/FilterPopup';

export type TableFilterState = {
    columnSort: ColumnSortModel | null;
    columnFilters: Record<string, FilterValue[]>;
    selectedRow: string | undefined;
    columnSearch: Record<string, string>;
};

const storeRegistry = new Map<string, FilterTableStore>();

export class FilterTableStore {
    private _tableKey?: string;

    @observable public columnSort: ColumnSortModel | null = null;
    @observable public columnFilters: ColumnFiltersMap = new Map();
    @observable public columnSearch: ColumnSearchMap = new Map();
    @observable public selectedRow: string | undefined;
    @observable public openedFilterField: string | null = null;
    @observable public columnWidths: Map<string, number> = new Map();

    public static getOrCreate(tableKey: string): FilterTableStore {
        if (!storeRegistry.has(tableKey)) {
            storeRegistry.set(tableKey, new FilterTableStore(tableKey));
        }
        return storeRegistry.get(tableKey)!;
    }

    public static clearRegistry(tableKey?: string) {
        if (tableKey) {
            storeRegistry.delete(tableKey);
        } else {
            storeRegistry.clear();
        }
    }

    constructor(tableKey?: string) {
        makeObservable(this);
        this._tableKey = tableKey;
        this._loadPersistedState();
    }

    @action
    private _loadPersistedState() {
        if (!this._tableKey) return;

        const raw = localStorage.getItem(this._storageKey);
        if (!raw) return;

        try {
            const state = JSON.parse(raw) as TableFilterState;

            this.columnFilters = new Map(Object.entries(state.columnFilters ?? {}));
            this.columnSearch  = new Map(Object.entries(state.columnSearch ?? {}));
            this.columnSort    = state.columnSort ?? null;
            this.selectedRow   = state.selectedRow;
        } catch {
            localStorage.removeItem(this._storageKey);
        }

        const rawWidths = localStorage.getItem(this._widthsStorageKey);
        if (!rawWidths) return;

        try {
            const widths = JSON.parse(rawWidths) as Record<string, number>;
            this.columnWidths = new Map(Object.entries(widths));
        } catch {
            localStorage.removeItem(this._widthsStorageKey);
        }
    }

    private get _storageKey(): string {
        return `tableFilters_${this._tableKey}`;
    }

    private get _widthsStorageKey(): string {
        return `tableColumnWidths_${this._tableKey}`;
    }

    private static _mapToObject<V>(map: Map<string, V>): Record<string, V> {
        const obj: Record<string, V> = {};
        map.forEach((value, key) => { obj[key] = value; });
        return obj;
    }

    private _persist() {
        if (!this._tableKey) return;

        const state: TableFilterState = {
            columnSort:    this.columnSort,
            columnFilters: FilterTableStore._mapToObject(this.columnFilters),
            columnSearch:  FilterTableStore._mapToObject(this.columnSearch),
            selectedRow:   this.selectedRow
        };
        localStorage.setItem(this._storageKey, JSON.stringify(state));
    }

    private _persistWidths() {
        if (!this._tableKey) return;

        localStorage.setItem(this._widthsStorageKey, JSON.stringify(FilterTableStore._mapToObject(this.columnWidths)));
    }

    @action.bound
    public setColumnWidths(widths: Record<string, number>) {
        this.columnWidths = new Map(Object.entries(widths));
        this._persistWidths();
    }

    @action.bound
    public resetColumnWidths() {
        this.columnWidths = new Map();

        if (this._tableKey) {
            localStorage.removeItem(this._widthsStorageKey);
        }
    }

    @action.bound
    public setColumnSort(key: string, sortType: ColumnSort) {
        this.columnSort = { key, sortType };
        this._persist();
    }

    @action.bound
    public setSelectedRow(selectedRow: string | undefined) {
        this.selectedRow = selectedRow;
        this._persist();
    }

    @action.bound
    public setColumnSearch(key: string, filter: string) {
        if (!filter) {
            this.columnSearch.delete(key);
        } else {
            this.columnSearch.set(key, filter);
        }
        this._persist();
    }

    @action.bound
    public setColumnFilters(key: string, filter: FilterValue) {
        const existing = this.columnFilters.get(key);

        if (existing) {
            const index = existing.indexOf(filter);

            if (index === -1) {
                // Replace array reference so MobX detects the change
                this.columnFilters.set(key, [...existing, filter]);
            } else {
                const updated = existing.filter((_, i) => i !== index);
                if (updated.length === 0) {
                    this.columnFilters.delete(key);
                } else {
                    this.columnFilters.set(key, updated);
                }
            }
        } else {
            this.columnFilters.set(key, [filter]);
        }

        this._persist();
    }

    @action.bound
    public setColumnFiltersMany(key: string, values: FilterValue[], add: boolean) {
        const existing = this.columnFilters.get(key) ?? [];
        let updated: FilterValue[];

        if (add) {
            const toAdd = values.filter(v => !existing.includes(v));
            updated = [...existing, ...toAdd];
        } else {
            updated = existing.filter(v => !values.includes(v));
        }

        if (updated.length === 0) {
            this.columnFilters.delete(key);
        } else {
            this.columnFilters.set(key, updated);
        }

        this._persist();
    }

    @action.bound
    public clearFilters() {
        this.columnSort    = null;
        this.columnFilters = new Map();
        this.columnSearch  = new Map();
        this.selectedRow   = undefined;

        if (this._tableKey) {
            localStorage.removeItem(this._storageKey);
        }
    }

    @action.bound
    public clearColumnSearchFilters() {
        this.columnSearch = new Map();
    }

    public getIsFilterActive(key: string): boolean {
        return !!this.columnFilters.get(key)?.length;
    }

    @action.bound
    public setOpenedFilterField(field: string | null) {
        this.openedFilterField = field;
    }

}